import { useState, useEffect, useCallback } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Button } from './ui/button';
import { Clock, AlertTriangle, RefreshCw } from 'lucide-react';

const WARNING_MINUTES = 5; // Show warning 5 minutes before expiry

export const SessionTimeoutModal = ({ token, onExtendSession, onLogout, onTokenRefresh, api }) => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isExtending, setIsExtending] = useState(false);
  const [sessionExpiry, setSessionExpiry] = useState(null);

  // Fetch session info on mount and periodically
  const fetchSessionInfo = useCallback(async () => {
    if (!token || !api) return;
    
    try {
      const response = await api.get('/auth/session-info');
      if (response.data?.expires_at) {
        setSessionExpiry(new Date(response.data.expires_at));
      }
    } catch (error) {
      if (error.response?.status === 401) {
        onLogout?.();
      }
    }
  }, [token, api, onLogout]);

  // Fetch session info on mount
  useEffect(() => {
    fetchSessionInfo();
    // Refresh session info every 2 minutes
    const interval = setInterval(fetchSessionInfo, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchSessionInfo]);

  // Check expiry and update countdown
  useEffect(() => {
    if (!sessionExpiry) return;

    const checkExpiry = () => {
      const now = new Date();
      const diff = sessionExpiry - now;
      const minutesRemaining = Math.floor(diff / 1000 / 60);
      const secondsRemaining = Math.floor((diff / 1000) % 60);
      
      if (diff <= 0) {
        // Session has expired
        setShowWarning(false);
        onLogout?.();
        return;
      }

      if (minutesRemaining < WARNING_MINUTES) {
        setShowWarning(true);
        setTimeRemaining({ minutes: minutesRemaining, seconds: secondsRemaining });
      } else {
        setShowWarning(false);
        setTimeRemaining(null);
      }
    };

    checkExpiry();
    const interval = setInterval(checkExpiry, 1000);
    return () => clearInterval(interval);
  }, [sessionExpiry, onLogout]);

  const handleExtendSession = async () => {
    setIsExtending(true);
    try {
      const response = await api.post('/auth/extend-session');
      // Impersonation sessions return a freshly-minted token — swap it in
      // atomically so subsequent requests (and session-info polls) use the new
      // expiry. Without this the countdown would revert to the old JWT expiry.
      if (response.data?.access_token) {
        onTokenRefresh?.(response.data.access_token);
      }
      if (response.data?.expires_at) {
        setSessionExpiry(new Date(response.data.expires_at));
        setShowWarning(false);
        onExtendSession?.();
      }
    } catch (error) {
      console.error('Failed to extend session:', error);
      if (error.response?.status === 401) {
        onLogout?.();
      }
    } finally {
      setIsExtending(false);
    }
  };

  const handleLogout = () => {
    setShowWarning(false);
    onLogout?.();
  };

  const formatTimeRemaining = () => {
    if (!timeRemaining) return '';
    const { minutes, seconds } = timeRemaining;
    if (minutes > 0) {
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${seconds} seconds`;
  };

  if (!showWarning) return null;

  return (
    <AlertDialog open={showWarning} onOpenChange={() => {}}>
      <AlertDialogContent className="sm:max-w-md" data-testid="session-timeout-modal">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
              <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <AlertDialogTitle className="text-lg" data-testid="session-timeout-title">
                Session Expiring Soon
              </AlertDialogTitle>
            </div>
          </div>
          <AlertDialogDescription className="pt-4 text-base" data-testid="session-timeout-description">
            Your session will expire in{' '}
            <span className="font-semibold text-amber-600 dark:text-amber-400" data-testid="session-timeout-countdown">
              {formatTimeRemaining()}
            </span>
            . Any unsaved changes may be lost.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
          <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          <p className="text-sm text-amber-700 dark:text-amber-300">
            Click "Stay Logged In" to extend your session, or log out now to secure your account.
          </p>
        </div>

        <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
          <Button 
            variant="outline" 
            onClick={handleLogout}
            className="w-full sm:w-auto"
            data-testid="session-timeout-logout-btn"
          >
            Log Out
          </Button>
          <AlertDialogAction asChild>
            <Button 
              onClick={handleExtendSession} 
              disabled={isExtending}
              className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="session-timeout-extend-btn"
            >
              {isExtending ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Extending...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Stay Logged In
                </>
              )}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default SessionTimeoutModal;
